// The walk-around's rules, apart from React so they can be tested on their own.
//
// Three things here were wrong on run 1 on BHUJA-PACK (23 Oct 2026):
//
// 1. A NUMERIC start-up item (a count of suction cups, cone fingers...) was shown
//    as Yes / No. "Yes" sent the value 1. The item wants the COUNT.
// 2. Those items carry RequiresEvidence = 1 - evidence on EVERY answer, not only
//    on a fail - and the screen never asked for it, so the server refused each
//    one with `evidence_required` (correctly: rule 4 kept the refusal).
// 3. The progress line counted the refused attempts as answered ("75 of 75"),
//    because it counted what the device had queued, not what the server took.
//    A refused attempt is not an answer (rules 4 and 15).

/** The parameter facts the rules below need. A subset of ReadinessItem['parameter']. */
export interface WalkParameter {
  valueKind: 'NUMERIC' | 'ATTRIBUTE';
  unit: string | null;
  requiresEvidenceOnFail: boolean;
  /** Optional: an API older than this build does not send them. */
  requiresEvidence?: boolean;
  nominal?: number | null;
  min?: number | null;
  max?: number | null;
}

/** An answer this device has queued: the value, and the capture id the queue minted for it. */
export interface LocalAnswer {
  value: number;
  clientCaptureId: string;
}

/**
 * Does this answer need an evidence note before it is sent?
 *
 * `requiresEvidence` is ALWAYS - whatever the answer. `requiresEvidenceOnFail`
 * is the forms' "if No then comment", and the server applies it to attribute
 * answers only (gateRules step 7), so the device does the same.
 */
export function needsEvidence(p: WalkParameter, value: number | null): boolean {
  if (p.requiresEvidence) return true;
  return p.requiresEvidenceOnFail && p.valueKind === 'ATTRIBUTE' && value === 0;
}

/**
 * The count the operator typed, or null when it is not a number.
 *
 * Strict on purpose: "24 each", "2 4" or "" is not 24. A count the device
 * guessed at is a count nobody took.
 */
export function parseCount(text: string): number | null {
  const t = text.trim();
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number, unit: string | null): string {
  return unit ? `${n} ${unit}` : String(n);
}

/** "Expected 24 each", "Expected 2 to 4 each", "At least 2", or null when the spec gives nothing. */
export function expectedText(p: WalkParameter): string | null {
  const min = p.min ?? null;
  const max = p.max ?? null;
  const nominal = p.nominal ?? null;
  if (min !== null && max !== null) {
    return min === max ? `Expected ${fmt(min, p.unit)}` : `Expected ${min} to ${fmt(max, p.unit)}`;
  }
  if (min !== null) return `At least ${fmt(min, p.unit)}`;
  if (max !== null) return `At most ${fmt(max, p.unit)}`;
  if (nominal !== null) return `Expected ${fmt(nominal, p.unit)}`;
  return null;
}

/** A PREVIEW only - the server decides. True when the count sits inside the spec's limits. */
export function countInSpec(p: WalkParameter, value: number): boolean {
  const min = p.min ?? null;
  const max = p.max ?? null;
  if (min !== null && value < min) return false;
  if (max !== null && value > max) return false;
  return true;
}

/**
 * Is this item answered?
 *
 * Yes when the server holds an ACCEPTED reading for it (the readiness reply
 * carries accepted readings only), or when this device has queued an answer
 * that the server has not refused. ⚠ A refused attempt is NOT an answer: the
 * check is still owed, and counting it is how "75 of 75 answered" appeared on a
 * line that could not be released.
 */
export function isAnswered(
  hasAcceptedReading: boolean,
  local: LocalAnswer | undefined,
  refusedIds: ReadonlySet<string>,
): boolean {
  if (hasAcceptedReading) return true;
  return local !== undefined && !refusedIds.has(local.clientCaptureId);
}

/** Drop the queued answers the server refused. Returns the same object when nothing changes. */
export function dropRefused(
  answers: Record<number, LocalAnswer>,
  refusedIds: ReadonlySet<string>,
): Record<number, LocalAnswer> {
  let changed = false;
  const out: Record<number, LocalAnswer> = {};
  for (const [k, a] of Object.entries(answers)) {
    if (refusedIds.has(a.clientCaptureId)) changed = true;
    else out[Number(k)] = a;
  }
  return changed ? out : answers;
}
